import { ComponentType } from "@angular/cdk/portal";
import {
    Component,
    contentChild,
    effect,
    inject,
    input,
    OnDestroy,
    output,
    signal,
    TemplateRef,
    untracked,
    ViewContainerRef
} from "@angular/core";
import { take } from "rxjs";
import { VdsDialog } from "./dialog";
import { VdsDialogConfig } from "./dialog-config";
import { VdsDialogRef } from "./dialog-ref";
import { VDS_DIALOG_TEMPLATE } from "./dialog-template";

@Component({
    selector: "vds-dialog",
    template: "",
    imports: [],
    host: { style: "display: contents" }
})
export class VdsDialogComponent<T = unknown, R = any> implements OnDestroy {
    private dialog = inject(VdsDialog);
    private viewContainerRef = inject(ViewContainerRef);
    private dialogRef = signal<VdsDialogRef<T, R> | null>(null);

    readonly open = input<boolean>(false);
    readonly config = input<VdsDialogConfig>({});

    readonly component = input<ComponentType<T> | undefined>();

    readonly dialogTpl = contentChild(VDS_DIALOG_TEMPLATE, { read: TemplateRef });

    readonly dialogClose = output<R | undefined>();

    constructor() {
        effect(() => {
            if (this.open()) {
                untracked(() => this.openDialog());
            } else {
                untracked(() => this.closeDialog());
            }
        });
    }

    ngOnDestroy() {
        if (this.dialogRef()) {
            this.dialogRef.set(null);
        }
    }

    openDialog(): void {
        const component = this.component();
        const dialogTpl = this.dialogTpl();

        if ((!dialogTpl && !component) || this.dialogRef()) {
            return;
        }

        const componentOrTemplate = (component ?? dialogTpl) as ComponentType<T> | TemplateRef<T>;
        const dialogRef = this.dialog.open<T, unknown, R>(componentOrTemplate, {
            viewContainerRef: this.viewContainerRef,
            ...this.config()
        });

        this.dialogRef.set(dialogRef);

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                this.dialogRef.set(null);
                this.dialogClose.emit(result);
            });
    }

    closeDialog(): void {
        this.dialogRef()?.close();
    }
}
